// Downloader de áudio (MP3) que delega o I/O pro plugin nativo (VxlBatchDownloader),
// mesma infra de background download dos segmentos HLS e do PDF.
//
// Diferente do PDF: a URL do áudio é a URL pública direta do CDN (vinda do grid em
// learningObject.audio), tocável pelo player online sem auth — então o download NÃO
// manda headers. Sem cifragem (paridade com o PDF): sandbox + isExcludedFromBackup
// (aplicados pelo plugin no destDir) dão a mesma proteção contra extração casual.

import { Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';
import { subscribeBatch } from '@/services/download/vxlBatchEventBus';
import { STORAGE_DIR, safeLoId, destDirFor, ensureDir } from '@/services/offline/offlinePaths';

// Nome fixo (o áudio do EAD é sempre .mp3) — remove() precisa de nome determinístico.
const AUDIO_FILENAME = 'audio.mp3';

// Prefixo `audio_` evita colisão com os batchIds de HLS (`lo_<id>`) e PDF (`pdf_<id>`).
function batchIdFor (loId) {
  return `audio_${safeLoId(loId)}`;
}

function awaitBatch (batchId, onProgress) {
  let resolveFn, rejectFn;
  const completion = new Promise((res, rej) => { resolveFn = res; rejectFn = rej; });
  let lastItemError = null;

  const unsubscribe = subscribeBatch(batchId, {
    progress: ({ done, total, percent }) => {
      const pct = percent != null ? Math.round(percent * 100) : Math.round((done / (total || 1)) * 100);
      if (typeof onProgress === 'function') onProgress(pct);
    },
    itemFailed: ({ itemId, error, httpStatus }) => {
      lastItemError = { itemId, error, httpStatus };
      console.warn(`[audioNativeDownloader] item ${itemId} falhou:`, error, httpStatus != null ? `(HTTP ${httpStatus})` : '');
    },
    completed: () => { unsubscribe(); resolveFn({ ok: true }); },
    failed: ({ error, failedItems }) => {
      unsubscribe();
      const httpPart = lastItemError && lastItemError.httpStatus != null ? ` (HTTP ${lastItemError.httpStatus})` : '';
      const reasonPart = lastItemError && lastItemError.error ? `: ${lastItemError.error}` : (error ? `: ${error}` : '');
      rejectFn(new Error(`Batch ${batchId} falhou${httpPart}${reasonPart}${failedItems ? ' [' + failedItems.join(',') + ']' : ''}`));
    }
  });

  return completion;
}

export const audioNativeDownloader = {
  async download (loId, audioUrl, onProgress, network) {
    if (!Capacitor.isNativePlatform()) return null;
    if (!audioUrl) throw new Error('audioUrl ausente pra download de áudio');

    const safeId = safeLoId(loId);
    if (!safeId) throw new Error('loId inválido pra download de áudio');

    const destDir = destDirFor(safeId);
    const batchId = batchIdFor(safeId);

    await ensureDir(destDir);

    const completionPromise = awaitBatch(batchId, onProgress);
    await VxlBatchDownloader.enqueue({
      batchId,
      destDir,
      concurrency: 1,
      items: [{ id: 'audio_main', url: audioUrl, filename: AUDIO_FILENAME }],
      ...(network ? { network } : {})
    });
    await completionPromise;

    try {
      await VxlBatchDownloader.cleanupBatch({ batchId });
    } catch {
      // best-effort
    }

    const { uri } = await Filesystem.getUri({
      path: `${destDir}/${AUDIO_FILENAME}`,
      directory: STORAGE_DIR
    });
    return Capacitor.convertFileSrc(uri);
  },

  async remove (loId) {
    // Idempotente: em LOs que nunca tiveram áudio, todos os passos viram no-op.
    const safeId = safeLoId(loId);
    if (!safeId) return;

    try {
      await VxlBatchDownloader.cancel({ batchId: batchIdFor(safeId) });
    } catch (e) {
      console.warn(`[audioNativeDownloader] cancel batch ${safeId} falhou:`, e?.message || e);
    }
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      await VxlBatchDownloader.cleanupBatch({ batchId: batchIdFor(safeId) });
    } catch (e) {
      console.warn(`[audioNativeDownloader] cleanupBatch ${safeId} falhou:`, e?.message || e);
    }
    // Apaga só o audio.mp3 — não o diretório (paridade com o PDF; o rmdir do
    // hlsNativeDownloader.remove(), chamado em paralelo, cuida do diretório).
    try {
      await Filesystem.deleteFile({
        path: `${destDirFor(safeId)}/${AUDIO_FILENAME}`,
        directory: STORAGE_DIR
      });
    } catch (e) {
      // ENOENT é o caso comum (LO sem áudio baixado).
    }
  }
};
